// 48kHz version of stream_processor for native Opus playback without resampling
// This version is optimized for Opus audio decoded at 48kHz to avoid resampling artifacts

export const StreamProcessor48kSrc = URL.createObjectURL(new Blob([`
  class StreamProcessor48k extends AudioWorkletProcessor {
    constructor() {
      super();
      this.buffers = {};
      this.trackIds = {};
      this.currentTrackId = null;
      this.volume = 1.0;
      this.sampleOffset = 0;
      // Playback jitter buffer target in milliseconds (configurable via message)
      this.minBufferMs = 400; // default buffer (will be adjusted by network monitor)
      this.minBufferSamples = 0; // computed from minBufferMs & sampleRate
      this.graceFrames = 0;
      this.maxGraceFrames = 50; // Increased grace frames for smoother playback
      this.completedTracks = new Set();
      this.totalSamplesProcessed = 0;
      this.sampleRate = 48000; // Native 48kHz for Opus
      // Maintain a FIFO queue of trackIds in order of arrival
      this.playQueue = [];
      // Track if we're in low-buffer state to prevent stuttering
      this.isBuffering = false;
      // Track last sample value for smooth transitions
      this.lastSample = 0;
      // When true, freeze playback position and output silence (used for user pause)
      this.isPaused = false;

      this.port.onmessage = (e) => {
        const { event } = e.data;
        if (event === 'write') {
          const { buffer, trackId } = e.data;
          
          // If this trackId was previously completed, reset it for new audio
          if (this.completedTracks.has(trackId)) {
            this.completedTracks.delete(trackId);
            delete this.buffers[trackId];
            delete this.trackIds[trackId];
          }

          if (!this.buffers[trackId]) {
            this.buffers[trackId] = new Float32Array(0);
            this.trackIds[trackId] = trackId;
            // New track: enqueue if not already present
            if (!this.playQueue.includes(trackId)) {
              this.playQueue.push(trackId);
            }
          }

          const floatBuffer = new Float32Array(buffer.length);
          for (let i = 0; i < buffer.length; i++) {
            floatBuffer[i] = buffer[i] / 32768.0;
          }

          const newBuffer = new Float32Array(this.buffers[trackId].length + floatBuffer.length);
          newBuffer.set(this.buffers[trackId]);
          newBuffer.set(floatBuffer, this.buffers[trackId].length);
          this.buffers[trackId] = newBuffer;
          // Only start playing when idle AND there is enough buffered audio; do NOT preempt current track
          if (this.currentTrackId === null) {
            this._pickNextTrack();
          } else if (this.currentTrackId === trackId) {
            // Keep current track alive
            this.graceFrames = 0;
          }

        } else if (event === 'offset' || event === 'interrupt') {
          const { requestId } = e.data;
          this.port.postMessage({
            event: 'offset',
            requestId,
            trackId: this.currentTrackId,
            offset: this.sampleOffset
          });

          if (event === 'interrupt') {
            // A barge-in / interrupt must drop ALL pending assistant audio, not
            // just the current track. With multi-item turns (a comment + the
            // post-tool-call answer) the queued follow-up track would otherwise
            // resume playing right after the user interrupted.
            this.buffers = {};
            this.trackIds = {};
            this.completedTracks.clear();
            this.playQueue = [];
            this.currentTrackId = null;
            this.sampleOffset = 0;
            this.totalSamplesProcessed = 0;
            this.graceFrames = 0;
            this.isBuffering = false;
            // Ensure we are not left in a paused state after an interrupt
            this.isPaused = false;
          }

        } else if (event === 'volume') {
          this.volume = e.data.volume;
        } else if (event === 'buffer.ms') {
          const { value } = e.data;
          const clamped = Math.max(0, Math.min(2000, Number(value) || 0));
          this.minBufferMs = clamped;
        } else if (event === 'track.complete') {
          const { trackId } = e.data;
          this.completedTracks.add(trackId);
          // If nothing is currently playing, a just-completed track sitting in the
          // queue (e.g. a short post-tool-call answer below the jitter threshold)
          // must start now — completion lets it bypass the buffer-fill wait.
          if (this.currentTrackId === null) {
            this._pickNextTrack();
          }
        } else if (event === 'track.activation.complete') {
          const { trackId } = e.data;
          this.completedTracks.add(trackId);
          if (this.currentTrackId === null) {
            this._pickNextTrack();
          }
        } else if (event === 'pause') {
          // Freeze playback position; audio buffers continue to accumulate
          this.isPaused = true;
        } else if (event === 'resume') {
          // Resume playback from the last sampleOffset
          this.isPaused = false;
        }
      };
    }

    process(inputs, outputs) {
      const output = outputs[0];
      const channel = output[0];
      this.sampleRate = sampleRate;
      // If paused, output silence but DO NOT advance playback position/state
      if (this.isPaused) {
        for (let i = 0; i < channel.length; i++) {
          channel[i] = 0;
        }
        return true;
      }
      // Update target min buffer samples each render quantum
      this.minBufferSamples = this._getMinBufferSamples();

      if (this.currentTrackId && this.buffers[this.currentTrackId]) {
        const buffer = this.buffers[this.currentTrackId];
        const remainingSamples = buffer.length - this.sampleOffset;
        const isTrackComplete = this.completedTracks.has(this.currentTrackId);

        if (remainingSamples > 0) {
          // Process as many samples as available, up to channel.length
          const samplesToProcess = Math.min(remainingSamples, channel.length);
          
          // Apply fade-in if resuming from buffering (prevents clicks when audio resumes)
          const wasBuffering = this.isBuffering;
          if (wasBuffering && samplesToProcess > 0) {
            const fadeInLength = Math.min(64, samplesToProcess); // 64 samples for smoother transitions
            for (let i = 0; i < fadeInLength; i++) {
              // Use cosine curve for smoother fade (more natural than linear)
              const fadeGain = 0.5 * (1 - Math.cos(Math.PI * i / fadeInLength));
              channel[i] = buffer[this.sampleOffset + i] * this.volume * fadeGain;
            }
            // Process remaining samples normally
            for (let i = fadeInLength; i < samplesToProcess; i++) {
              channel[i] = buffer[this.sampleOffset + i] * this.volume;
            }
          } else {
            // Normal processing
            for (let i = 0; i < samplesToProcess; i++) {
              channel[i] = buffer[this.sampleOffset + i] * this.volume;
            }
          }
          
          // Apply fade-out to prevent clicks when chunk ends mid-frame
          if (samplesToProcess < channel.length) {
            const fadeLength = Math.min(64, samplesToProcess); // 64 samples (~1.3ms at 48kHz) for smoother transitions
            for (let i = 0; i < fadeLength; i++) {
              const fadeIdx = samplesToProcess - fadeLength + i;
              if (fadeIdx >= 0) {
                // Use cosine curve for smoother fade (more natural than linear)
                const fadeGain = 0.5 * (1 + Math.cos(Math.PI * i / fadeLength));
                channel[fadeIdx] *= fadeGain;
              }
            }
            // Fill the rest with silence
            for (let i = samplesToProcess; i < channel.length; i++) {
              channel[i] = 0;
            }
          }
          
          // Track last sample for smooth transitions
          if (samplesToProcess > 0) {
            this.lastSample = channel[samplesToProcess - 1];
          }
          
          this.sampleOffset += samplesToProcess;
          this.totalSamplesProcessed += samplesToProcess;
          this.graceFrames = 0;
          this.isBuffering = false;

        } else if (this.sampleOffset >= buffer.length && !isTrackComplete) {
          // Track is still streaming but we've consumed all buffered samples
          // Wait (output silence) to allow more data to arrive - this prevents stuttering
          this.graceFrames++;
          
          // Log when we first enter buffering state
          if (this.graceFrames === 1) {
            this.port.postMessage({
              event: 'buffering_start',
              trackId: this.currentTrackId,
              bufferLength: buffer.length,
              sampleOffset: this.sampleOffset
            });
          }
          
          for (let i = 0; i < channel.length; i++) {
            channel[i] = 0;
          }
          this.isBuffering = true;
          
          // If we've been waiting too long, log a warning
          if (this.graceFrames === this.maxGraceFrames) {
            this.port.postMessage({
              event: 'buffer_underrun',
              trackId: this.currentTrackId,
              graceFrames: this.graceFrames
            });
          }
          return true;

        } else if (this.sampleOffset >= buffer.length && isTrackComplete) {
          const bufferDurationSeconds = buffer.length / this.sampleRate;
          const processedDurationSeconds = this.totalSamplesProcessed / this.sampleRate;

          if (this.graceFrames < this.maxGraceFrames) {
            this.graceFrames++;
            for (let i = 0; i < channel.length; i++) {
              channel[i] = 0;
            }
            return true;
          }

          if (processedDurationSeconds >= bufferDurationSeconds) {
            const finishedTrackId = this.currentTrackId;
            const finishedBufferLength = buffer.length;
            const finishedOffset = this.sampleOffset;
            // Free the finished track's buffer. The node is now long-lived for the
            // duration of a turn (it is only torn down once the whole queue
            // drains), so per-track buffers must be released here to avoid
            // unbounded memory growth across a long session.
            delete this.buffers[finishedTrackId];
            delete this.trackIds[finishedTrackId];

            // Advance to the next queued track (FIFO). A tool/function call emits
            // multiple assistant items in one turn (a comment before the call,
            // then the answer after it); the later item is buffered while this one
            // is still playing.
            this.currentTrackId = null;
            this.sampleOffset = 0;
            this.totalSamplesProcessed = 0;
            this.graceFrames = 0;
            this._pickNextTrack();

            // A turn can contain multiple assistant items (e.g. a comment before
            // a tool/function call, then the answer after it). queueDrained tells
            // the player whether this was the LAST track of the turn. The node is
            // NEVER torn down here — it persists for the whole session so a queued
            // follow-up track stays audible (tearing it down was the root cause of
            // "audio silent after a tool call").
            const queueDrained = this.currentTrackId === null && this.playQueue.length === 0;
            this.port.postMessage({
              event: 'track_finished',
              trackId: finishedTrackId,
              queueDrained,
              bufferLength: finishedBufferLength,
              finalOffset: finishedOffset
            });

            for (let i = 0; i < channel.length; i++) {
              channel[i] = 0;
            }
          } else {
            for (let i = 0; i < channel.length; i++) {
              channel[i] = 0;
            }
          }
        } else {
          // Output silence if track not completed or no samples to process
          for (let i = 0; i < channel.length; i++) {
            channel[i] = 0;
          }
        }
      } else {
        // No track, output silence
        for (let i = 0; i < channel.length; i++) {
          channel[i] = 0;
        }
      }

      return true;
    }

    // Select the next track to play from the FIFO queue and make it current.
    // Robust against the multi-item case (e.g. a commentary message followed by
    // the final answer after a tool call): never drop a track that still has
    // buffered audio, and play an already-completed track immediately instead of
    // waiting for the jitter threshold (no more data is coming for it).
    _pickNextTrack() {
      const minSamples = this._getMinBufferSamples();
      while (this.playQueue.length > 0) {
        const nextId = this.playQueue[0];
        const buf = this.buffers[nextId];
        const isDone = this.completedTracks.has(nextId);
        if (!buf || buf.length === 0) {
          // Nothing buffered. If the track already finished it has nothing to
          // play, so drop it; otherwise wait for its data to arrive.
          if (isDone) { this.playQueue.shift(); continue; }
          break;
        }
        // Start when we have enough buffered for smooth playback, OR when the
        // track is already complete (waiting longer can't help).
        if (isDone || buf.length >= minSamples) {
          this.playQueue.shift();
          this.currentTrackId = nextId;
          this.sampleOffset = 0;
          this.totalSamplesProcessed = 0;
          this.graceFrames = 0;
          this.isBuffering = false;
          break;
        }
        // Has audio but not enough yet and not complete - wait for more data.
        break;
      }
    }

    _getMinBufferSamples() {
      const sr = this.sampleRate || 48000;
      const ms = this.minBufferMs || 0;
      const samples = Math.max(0, Math.floor((sr * ms) / 1000));
      return samples;
    }
  }

  registerProcessor('stream_processor_48k', StreamProcessor48k);
`], { type: 'application/javascript' }));
